import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import {
  COSMETICS,
  DEFAULT_SKIN,
  DEFAULT_THEME,
  equipValue,
  type CosmeticDef,
} from "../game/cosmetics";
import { progress } from "../store/progress";
import { settings } from "../store/settings";
import { tierSwatch } from "../render/renderer";

/**
 * Cosmetics shop (ideas #10/#11): buy tile skins and board themes with
 * stardust, equip them instantly. Defaults are free and always available.
 */
export class CosmeticsScreen implements Screen {
  private off?: () => void;
  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    const screen = el("div", "screen cosmetics");
    const top = buildTopbar({
      title: "menu.cosmetics",
      onBack: () => this.nav.home(),
      showStardust: true,
    });

    const body = el("div", "cosmetics-body");

    const render = (): void => {
      top.refresh();
      body.innerHTML = "";

      body.appendChild(el("div", "cos-head", t("cos.skins")));
      const skins = el("div", "cos-list");
      skins.appendChild(this.freeRow("skin", DEFAULT_SKIN, render));
      for (const c of COSMETICS.filter((x) => x.type === "skin")) {
        skins.appendChild(this.shopRow(c, render));
      }
      body.appendChild(skins);

      body.appendChild(el("div", "cos-head", t("cos.themes")));
      const themes = el("div", "cos-list");
      themes.appendChild(this.freeRow("theme", DEFAULT_THEME, render));
      for (const c of COSMETICS.filter((x) => x.type === "theme")) {
        themes.appendChild(this.shopRow(c, render));
      }
      body.appendChild(themes);
    };

    screen.append(top.node, body);
    host.appendChild(screen);
    render();
    this.off = i18n.onChange(render);
  }

  private equippedValue(type: "skin" | "theme"): string {
    return type === "skin" ? settings.get("tileSkin") : settings.get("boardTheme");
  }

  private equip(type: "skin" | "theme", value: string): void {
    if (type === "skin") settings.set("tileSkin", value);
    else settings.set("boardTheme", value);
  }

  /** Mini board-tile preview rendered under the cosmetic's skin/theme. */
  private preview(type: "skin" | "theme", value: string): string {
    if (type === "skin") {
      return `<span class="cos-preview" data-skin="${value}">${tierSwatch(5, 44)}</span>`;
    }
    return `<span class="cos-preview cos-theme-chip" data-board-theme="${value}"></span>`;
  }

  /** The free default entry for each category. */
  private freeRow(type: "skin" | "theme", value: string, render: () => void): HTMLElement {
    const equipped = this.equippedValue(type) === value;
    const row = el("div", `cos-row ${equipped ? "equipped" : ""}`);
    row.innerHTML = `
      ${this.preview(type, value)}
      <span class="cos-info">
        <span class="cos-name">${t(`cos.${type}.${value}`)}</span>
        <span class="cos-sub">${t("cos.default")}</span>
      </span>`;
    const btn = el("button", equipped ? "cos-btn" : "cos-btn btn-primary");
    btn.textContent = equipped ? t("cos.equipped") : t("cos.equip");
    btn.disabled = equipped;
    btn.onclick = () => {
      this.equip(type, value);
      render();
    };
    row.appendChild(btn);
    return row;
  }

  private shopRow(c: CosmeticDef, render: () => void): HTMLElement {
    const value = equipValue(c.id);
    const owned = progress.ownsCosmetic(c.id);
    const equipped = owned && this.equippedValue(c.type) === value;
    const affordable = progress.snapshot.stardust >= c.cost;

    const row = el("div", `cos-row ${equipped ? "equipped" : ""}`);
    row.innerHTML = `
      ${this.preview(c.type, value)}
      <span class="cos-info">
        <span class="cos-name">${c.icon} ${t(`cos.${c.type}.${value}`)}</span>
        <span class="cos-sub">${t(`cos.${c.type}.${value}.desc`)}</span>
      </span>`;

    const btn = el("button", "cos-btn");
    if (equipped) {
      btn.textContent = t("cos.equipped");
      btn.disabled = true;
    } else if (owned) {
      btn.textContent = t("cos.equip");
      btn.classList.add("btn-primary");
      btn.onclick = () => {
        this.equip(c.type, value);
        render();
      };
    } else {
      btn.innerHTML = `<span class="sd-icon">✦</span> ${c.cost}`;
      if (affordable) btn.classList.add("btn-primary");
      btn.disabled = !affordable;
      btn.onclick = () => {
        if (progress.buyCosmetic(c.id, c.cost)) {
          this.equip(c.type, value);
          render();
        }
      };
    }
    row.appendChild(btn);
    return row;
  }

  unmount(): void {
    this.off?.();
  }
}
